import { useEffect, useState } from "react";
import { InputNumber, Input, Typography } from "antd";

import InputDefinition from "@/components/inputs/InputDefinition";
import InputEmployee from "@/components/inputs/InputEmployee";
import InputCalendar from "@/components/inputs/InputCalendar";
import UploadFile from "@/components/inputs/UploadFile";
import InputMoney from "@/components/inputs/InputMoney";
import InputMask from "@/components/inputs/InputMask";
import PolygonField from "@/components/views/ObjectFieldValue/PolygonField";
import PointField from "@/components/views/ObjectFieldValue/PointField";
import TableField from "@/components/views/ObjectFieldValue/TableField";

import {
  NUMBER_FIELDS,
  DEFINITIONS,
  UPLOAD_FIELDS,
  DATE_FIELDS,
} from "@/constants/definitions";
import {
  isIntegerInputInRange,
  isNumberInputInRange,
  normalizeDecimalInput,
  NUMBER_INPUT_RANGE_MESSAGE,
  parseNumberInput,
} from "@/utils/fieldValueConverter";

const EditFieldValue = ({
  value,
  field,
  unit_uuid,
  units = [],
  isAdmins = false,
  color,
  isNew = false,
  object_uuid,
  object_name,
  definition_uuid,
  object_unit_uuid,
  onChange,
  onKeyDown,
  onValidate,
  onComplete,
  onRefresh,
  onRelatedTasksRefresh,
  canEditObject,
  valueAsObject = false,
}) => {
  switch (true) {
    // Всегда перед NUMBER_FIELDS
    case field.type === "money":
      return (
        <InputMoney
          value={value}
          className="w-100"
          onChange={onChange}
          onKeyDown={onKeyDown}
          onValidate={onValidate}
        />
      );
    case NUMBER_FIELDS.includes(field.type):
      return (
        <CustomInputNumber
          value={value}
          type={field.type}
          onKeyDown={onKeyDown}
          onChange={onChange}
          onValidate={onValidate}
        />
      );
    case DATE_FIELDS.includes(field.type):
      return (
        <InputCalendar
          value={value}
          type={field.type}
          className="w-100"
          onSubmit={onChange}
          onKeyDown={onKeyDown}
          onComplete={onComplete}
        />
      );
    case field.type === "employee":
      return (
        <InputEmployee
          className="w-100"
          value={value}
          onSubmit={onChange}
          onKeyDown={onKeyDown}
          onComplete={onComplete}
          returnObject={valueAsObject}
        />
      );
    case DEFINITIONS.includes(field.type):
      return (
        <InputDefinition
          className="w-100"
          definitionId={field.object}
          fieldType={field.type}
          value={value}
          unit_uuid={object_unit_uuid}
          isAdmins={isAdmins}
          returnObject={valueAsObject}
          onSubmit={onChange}
          onKeyDown={onKeyDown}
          onComplete={onComplete}
        />
      );
    case UPLOAD_FIELDS.includes(field.type):
      return (
        <UploadFile
          value={value}
          accept={field.type === "image" ? "image/png, image/jpeg" : ""}
          unit_uuid={object_unit_uuid}
          units={units}
          returnObject={valueAsObject}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onComplete={onComplete}
        />
      );

    case field.type === "point":
      return (
        <PointField
          field={field}
          objectUuid={object_uuid}
          value={value}
          showEmpty
          isEdit
          color={color}
          onChange={onChange}
          onComplete={onComplete}
          onKeyDown={onKeyDown}
          height={onKeyDown ? 160 : null}
        />
      );
    case field.type === "polygon":
      return (
        <PolygonField
          field={field}
          objectUuid={object_uuid}
          value={value}
          showEmpty
          isEdit
          color={color}
          onChange={onChange}
          onComplete={onComplete}
          onKeyDown={onKeyDown}
          height={onKeyDown ? 160 : null}
        />
      );
    case field.type === "table":
      return isNew ? (
        <Typography.Text
          type="secondary"
          style={{
            display: "block",
            paddingBlock: 5,
          }}
        >
          Необходимо создать объект
        </Typography.Text>
      ) : (
        <TableField
          title={field.name}
          value={value}
          object_uuid={object_uuid}
          object_name={object_name}
          definition_uuid={definition_uuid}
          isAdmins={isAdmins}
          onRefresh={onRefresh}
          onRelatedTasksRefresh={onRelatedTasksRefresh}
          unit_uuid={unit_uuid}
          parent_field={field}
          isEditable
          object_unit_uuid={object_unit_uuid}
          canEditObject={canEditObject}
          units={units}
        />
      );
    case field.type === "rpoint":
      return <></>;
    case field.type === "parent_polygon":
      return <PolygonField value={value} miniature color={color} />;
    case field.type === "mask":
      return (
        <InputMask
          value={value}
          field={field}
          onKeyDown={onKeyDown}
          onChange={onChange}
          onValidate={onValidate}
        />
      );
    default:
      return (
        <InputString value={value} onKeyDown={onKeyDown} onChange={onChange} />
      );
  }
};
export default EditFieldValue;

const InputString = ({ value, onKeyDown, onChange }) => {
  const [val, setVal] = useState(value);

  useEffect(() => {
    setVal(value);
  }, [value]);

  return (
    <Input
      value={val}
      onChange={(e) => {
        setVal(e.target.value);
        onChange?.(e.target.value);
      }}
      className="w-100"
      onKeyDown={(e) => onKeyDown?.(e, val)}
    />
  );
};
const CustomInputNumber = ({
  value,
  type,
  onKeyDown,
  onChange,
  onValidate,
}) => {
  const [val, setVal] = useState(value);
  const validateValue =
    type === "integer" ? isIntegerInputInRange : isNumberInputInRange;
  const rangeMessage = NUMBER_INPUT_RANGE_MESSAGE;
  const isValid = validateValue(val);

  useEffect(() => {
    setVal(value);
  }, [value]);

  useEffect(() => {
    onValidate?.(isValid);
    // onValidate часто передается как inline-функция, поэтому следим только за значением.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid]);

  const handlePasteNumber = (e) => {
    e.preventDefault();
    const paste = (e.clipboardData || window.clipboardData).getData("text");
    const normalizedValue = normalizeDecimalInput(paste);
    const numericValue = parseNumberInput(normalizedValue);
    const nextValue = validateValue(normalizedValue)
      ? numericValue
      : normalizedValue;
    setVal(nextValue);
    onChange?.(nextValue);
  };

  return (
    <div className="w-100">
      <InputNumber
        value={val}
        step={type === "integer" ? 1 : 0.1}
        precision={type === "integer" ? 0 : undefined}
        status={isValid ? undefined : "error"}
        className="w-100"
        onChange={(num) => {
          setVal(num);
          onChange?.(num);
        }}
        onPaste={handlePasteNumber}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !isValid) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }

          onKeyDown?.(e, val);
        }}
      />
      {!isValid && (
        <Typography.Text type="danger" style={{ display: "block", fontSize: 12 }}>
          {rangeMessage}
        </Typography.Text>
      )}
    </div>
  );
};
